import { useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";
import type { PublicacaoDto } from "@base/contracts";
import { EstadoVazio, MolduraDoApp } from "@base/ui";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import {
  agruparPublicacoesAgenda,
  ancoraAoTrocarVista,
  chaveGrupoPublicacao,
  ancoraDaVista,
  avancarPeriodo,
  celulasDoMes,
  chaveDoDia,
  diasDaSemana,
  intervaloDaVista,
  mesmoDia,
  mesmoMes,
  rotuloDoPeriodo,
  type GrupoPublicacaoAgenda,
  type VistaDaAgenda,
} from "../domain/calendario-agenda";
import { useCalendarioPublicacoes } from "../application/use-publicacoes";
import { CartaoPublicacaoAgenda } from "./cartao-publicacao-agenda";
import { ModalDetalheFalhaPublicacao } from "./modal-detalhe-falha-publicacao";
import { ModalNovoAgendamento } from "./modal-novo-agendamento";
import { ModalPreviewPublicacaoAgenda } from "./modal-preview-publicacao-agenda";

function isoDe(d: Date): string {
  return d.toISOString();
}

function gruposDoDia(
  itens: readonly PublicacaoDto[] | undefined,
  dia: Date,
): GrupoPublicacaoAgenda[] {
  const chave = chaveDoDia(dia);
  return agruparPublicacoesAgenda(
    (itens ?? []).filter(
      (p) =>
        p.agendarPara != null &&
        chaveDoDia(new Date(p.agendarPara)) === chave,
    ),
  );
}

const DIAS_CABECALHO = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export function PaginaAgenda() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("agenda");
  const [vista, setVista] = useState<VistaDaAgenda>("semana");
  const [ancora, setAncora] = useState(() => ancoraDaVista("semana"));
  const [modalAberto, setModalAberto] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<readonly PublicacaoDto[] | null>(null);
  const [falhasDetalhe, setFalhasDetalhe] = useState<
    readonly PublicacaoDto[] | null
  >(null);

  const { de, ate } = useMemo(
    () => intervaloDaVista(vista, ancora),
    [vista, ancora],
  );
  const { data, isLoading, isError, error } = useCalendarioPublicacoes(
    isoDe(de),
    isoDe(ate),
  );

  const hoje = useMemo(() => new Date(), []);
  const rotulo = rotuloDoPeriodo(vista, ancora);
  const diasSemana = useMemo(() => diasDaSemana(ancora), [ancora]);
  const celulasMes = useMemo(() => celulasDoMes(ancora), [ancora]);

  function trocarVista(proxima: VistaDaAgenda) {
    setVista(proxima);
    setAncora((atual) => ancoraAoTrocarVista(proxima, atual));
  }

  function irParaHoje() {
    setAncora(ancoraDaVista(vista, new Date()));
  }

  const grupoDoPreview = useMemo(() => {
    const alvo = (data?.itens ?? []).find((p) => p.id === previewId);
    if (!alvo) return [];
    const chave = chaveGrupoPublicacao(alvo);
    return agruparPublicacoesAgenda(
      (data?.itens ?? []).filter((p) => chaveGrupoPublicacao(p) === chave),
    )[0]?.itens ?? [alvo];
  }, [data?.itens, previewId]);

  /** Edita o card inteiro: todas as redes criadas juntas com este post. */
  function editarGrupoDe(publicacao: PublicacaoDto) {
    const chave = chaveGrupoPublicacao(publicacao);
    const grupo = (data?.itens ?? []).filter(
      (p) => chaveGrupoPublicacao(p) === chave,
    );
    setPreviewId(null);
    setEdicao(grupo.length > 0 ? grupo : [publicacao]);
    setModalAberto(true);
  }

  const semItens = !isLoading && !isError && (data?.itens.length ?? 0) === 0;

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Agenda"
      subtitulo="Calendário de publicações: dia, semana ou mês"
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
      acoes={
        <Button
          type="button"
          className="cursor-pointer"
          onClick={() => setModalAberto(true)}
        >
          <Plus className="size-4" aria-hidden />
          Novo agendamento
        </Button>
      }
    >
      <PageLayout className="animate-aparecer">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="cursor-pointer"
              aria-label="Período anterior"
              onClick={() => setAncora((a) => avancarPeriodo(vista, a, -1))}
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              onClick={irParaHoje}
            >
              Hoje
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="cursor-pointer"
              aria-label="Próximo período"
              onClick={() => setAncora((a) => avancarPeriodo(vista, a, 1))}
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
            <p className="min-w-0 text-sm font-medium capitalize tracking-tight text-foreground sm:ml-1">
              {rotulo}
            </p>
          </div>

          <ToggleGroup
            type="single"
            value={vista}
            onValueChange={(v) => {
              if (v === "dia" || v === "semana" || v === "mes") {
                trocarVista(v);
              }
            }}
            variant="outline"
            size="sm"
            className="justify-start"
            aria-label="Vista do calendário"
          >
            <ToggleGroupItem value="dia" className="cursor-pointer px-3">
              Dia
            </ToggleGroupItem>
            <ToggleGroupItem value="semana" className="cursor-pointer px-3">
              Semana
            </ToggleGroupItem>
            <ToggleGroupItem value="mes" className="cursor-pointer px-3">
              Mês
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {isLoading ? (
          <div
            className={cn(
              "grid gap-3",
              vista === "dia" && "grid-cols-1",
              vista === "semana" && "md:grid-cols-7",
              vista === "mes" && "grid-cols-7",
            )}
          >
            {Array.from({
              length: vista === "dia" ? 3 : vista === "semana" ? 7 : 28,
            }).map((_, i) => (
              <Skeleton
                key={i}
                className={vista === "mes" ? "h-24" : "h-40"}
              />
            ))}
          </div>
        ) : null}

        {isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Falha ao carregar a agenda</AlertTitle>
            <AlertDescription>
              {(error as Error)?.message ?? "Tente de novo em instantes."}
            </AlertDescription>
          </Alert>
        ) : null}

        {!isLoading && !isError && vista === "dia" && semItens ? (
          <EstadoVazio
            icone={<CalendarDays className="size-8" aria-hidden />}
            titulo="Nada agendado neste dia"
            descricao="Agende um post para vê-lo listado neste dia."
          />
        ) : null}

        {!isLoading && !isError && vista === "dia" && !semItens ? (
          <VistaDia
            dia={ancora}
            itens={gruposDoDia(data?.itens, ancora)}
            ehHoje={mesmoDia(ancora, hoje)}
            onVerPreview={(p) => setPreviewId(p.id)}
            onVerErro={setFalhasDetalhe}
          />
        ) : null}

        {!isLoading && !isError && vista === "semana" ? (
          <VistaSemana
            dias={diasSemana}
            itens={data?.itens}
            hoje={hoje}
            onVerPreview={(p) => setPreviewId(p.id)}
            onVerErro={setFalhasDetalhe}
          />
        ) : null}

        {!isLoading && !isError && vista === "mes" ? (
          <VistaMes
            celulas={celulasMes}
            ancora={ancora}
            itens={data?.itens}
            hoje={hoje}
            onEscolherDia={(dia) => {
              setAncora(ancoraDaVista("dia", dia));
              setVista("dia");
            }}
          />
        ) : null}
      </PageLayout>

      <ModalNovoAgendamento
        aberto={modalAberto}
        edicao={edicao}
        onFechar={() => {
          setModalAberto(false);
          setEdicao(null);
        }}
      />
      <ModalPreviewPublicacaoAgenda
        publicacaoId={previewId}
        onFechar={() => setPreviewId(null)}
        onEditar={editarGrupoDe}
        grupo={grupoDoPreview}
      />
      <ModalDetalheFalhaPublicacao
        falhas={falhasDetalhe}
        onFechar={() => setFalhasDetalhe(null)}
      />
    </MolduraDoApp>
  );
}

function VistaDia({
  dia,
  itens,
  ehHoje,
  onVerPreview,
  onVerErro,
}: {
  readonly dia: Date;
  readonly itens: readonly GrupoPublicacaoAgenda[];
  readonly ehHoje: boolean;
  readonly onVerPreview: (publicacao: PublicacaoDto) => void;
  readonly onVerErro: (falhas: readonly PublicacaoDto[]) => void;
}) {
  const cabecalho = dia.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  if (itens.length === 0) return null;
  return (
    <section
      className={cn(
        "rounded-xl border border-border bg-muted/20 p-4 sm:p-6",
        ehHoje && "ring-1 ring-ring/30",
      )}
      aria-label={`Publicações de ${chaveDoDia(dia)}`}
    >
      <header className="mb-5 border-b border-border pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {ehHoje ? "Hoje" : "Dia"}
        </p>
        <h2 className="mt-1 text-xl font-semibold tracking-tight capitalize text-foreground sm:text-2xl">
          {cabecalho}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {itens.length} publicação{itens.length === 1 ? "" : "ões"}
        </p>
      </header>
      <ul className="mx-auto grid max-w-3xl gap-4">
        {itens.map((grupo) => (
          <CartaoPublicacaoAgenda
            key={grupo.chave}
            publicacao={grupo.representativo}
            redes={grupo.itens}
            densidade="dia"
            onVerPreview={onVerPreview}
            onVerErro={onVerErro}
          />
        ))}
      </ul>
    </section>
  );
}

function VistaSemana({
  dias,
  itens,
  hoje,
  onVerPreview,
  onVerErro,
}: {
  readonly dias: readonly Date[];
  readonly itens: readonly PublicacaoDto[] | undefined;
  readonly hoje: Date;
  readonly onVerPreview: (publicacao: PublicacaoDto) => void;
  readonly onVerErro: (falhas: readonly PublicacaoDto[]) => void;
}) {
  return (
    <div className="grid gap-4">
      {dias.map((dia) => {
        const doDia = gruposDoDia(itens, dia);
        const ehHoje = mesmoDia(dia, hoje);
        return (
          <section
            key={chaveDoDia(dia)}
            className={cn(
              "rounded-xl border border-border-subtle bg-background p-4 transition-colors duration-150",
              ehHoje && "border-primary/60",
            )}
          >
            <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h3
                className={cn(
                  "text-base font-semibold capitalize tracking-tight text-foreground",
                  !ehHoje && "text-foreground/90",
                )}
              >
                {dia.toLocaleDateString("pt-BR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "short",
                })}
                {ehHoje ? (
                  <span className="ml-2 inline-flex h-5 items-center rounded-full bg-primary px-2 align-middle text-[11px] font-semibold normal-case text-primary-foreground">
                    Hoje
                  </span>
                ) : null}
              </h3>
              <p className="text-xs text-muted-foreground">
                {doDia.length === 0
                  ? "Nada agendado"
                  : `${doDia.length} post${doDia.length === 1 ? "" : "s"}`}
              </p>
            </header>
            {doDia.length === 0 ? null : (
              <ul className="grid gap-2">
                {doDia.map((grupo) => (
                  <CartaoPublicacaoAgenda
                    key={grupo.chave}
                    publicacao={grupo.representativo}
                    redes={grupo.itens}
                    densidade="semana"
                    onVerPreview={onVerPreview}
                    onVerErro={onVerErro}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function VistaMes({
  celulas,
  ancora,
  itens,
  hoje,
  onEscolherDia,
}: {
  readonly celulas: readonly Date[];
  readonly ancora: Date;
  readonly itens: readonly PublicacaoDto[] | undefined;
  readonly hoje: Date;
  readonly onEscolherDia: (dia: Date) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {DIAS_CABECALHO.map((nome) => (
          <div
            key={nome}
            className="px-1 py-2 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
          >
            {nome}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-[minmax(6.5rem,auto)]">
        {celulas.map((dia) => {
          const noMes = mesmoMes(dia, ancora);
          const ehHoje = mesmoDia(dia, hoje);
          const doDia = gruposDoDia(itens, dia);
          return (
            <button
              key={chaveDoDia(dia)}
              type="button"
              onClick={() => onEscolherDia(dia)}
              className={cn(
                "flex min-h-[6.5rem] cursor-pointer flex-col gap-1 border-b border-r border-border p-1.5 text-left transition-colors duration-150",
                "hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                !noMes && "bg-muted/30 text-muted-foreground",
                ehHoje && "bg-accent/40",
              )}
              aria-label={`Abrir dia ${chaveDoDia(dia)}`}
            >
              <span
                className={cn(
                  "inline-flex size-6 items-center justify-center rounded-full text-xs tabular-nums",
                  ehHoje && "bg-primary text-primary-foreground",
                  !noMes && "opacity-50",
                )}
              >
                {dia.getDate()}
              </span>
              <ul className="grid min-w-0 flex-1 gap-0.5 content-start">
                {doDia.slice(0, 3).map((grupo) => (
                  <CartaoPublicacaoAgenda
                    key={grupo.chave}
                    publicacao={grupo.representativo}
                    redes={grupo.itens}
                    densidade="mes"
                  />
                ))}
                {doDia.length > 3 ? (
                  <li className="px-1 text-[10px] text-muted-foreground">
                    +{doDia.length - 3}
                  </li>
                ) : null}
              </ul>
            </button>
          );
        })}
      </div>
    </div>
  );
}
